import { Box, Button, CircularProgress, Grid, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React, { useMemo, useState } from "react";
import DropdownMenu from "common/DropDownMenu/DropDownMenu";
import FormDialog from "common/Modal/Modal";
import { enqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { ItemsProfileService } from "subModule/src/core/services";
import {
  ItemExcelUpdatePreviewResponse,
  ItemExcelUpdateResponse,
  ItemExcelUpdateRow,
} from "subModule/src/services/items/interface";
import { UploadPreviewTable } from "common/Misc/UploadPreviewTable";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";

type ItemBulkUpdateMenuProps = {
  refetch?: () => void;
  selectedRows?: any[];
  pageRows?: any[];
};

const normalizePreviewRows = (
  data: ItemExcelUpdatePreviewResponse["data"],
): ItemExcelUpdateRow[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.content)) return data.content;
  }
  return [];
};

const rowHasErrors = (row: ItemExcelUpdateRow) => {
  if (Array.isArray(row.invalidValues) && row.invalidValues.length) return true;
  if (Array.isArray(row.errors) && row.errors.length) return true;
  if (typeof row.errors === "string" && row.errors.trim()) return true;
  if (typeof row.error === "string" && row.error.trim()) return true;
  return false;
};

const ItemBulkUpdateMenu: React.FC<ItemBulkUpdateMenuProps> = ({
  refetch,
  selectedRows = [],
  pageRows = [],
}) => {
  const [modal, setModal] = useState(false);
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const currency = loggedInUserData?.business?.currency;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [imports, setImports] = useState<ItemExcelUpdateRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [summary, setSummary] = useState<{
    total: number;
    updated: number;
    failed: number;
  } | null>(null);

  const columns = [
    { id: "id", label: t("id") || "ID", numeric: false },
    { id: "itemCode", label: `${t("itemCode")} *`, numeric: false },
    {
      id: "title",
      label: `${!primaryLanguage ? t("primary") : ""} ${t("title")} ${
        primaryLanguage ? ` (${t(primaryLanguage)})` : ""
      } *`,
      numeric: false,
    },
    {
      id: "secondaryTitle",
      label: `${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${
        secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""
      }`,
      numeric: false,
    },
    {
      id: "itemCategoryTitle",
      label: `${t("itemCategory")} *`,
      numeric: false,
    },
    { id: "manufacturerTitle", label: t("manufacturer"), numeric: false },
    { id: "unitTitle", label: `${t("baseUnit")} *`, numeric: false },
    { id: "itemGroupTitle", label: t("itemGroup"), numeric: false },
    { id: "partNo", label: t("partNo"), numeric: false },
    { id: "taxable", label: t("taxable"), numeric: false },
    { id: "taxProcedureTitle", label: t("taxProcedure"), numeric: false },
    { id: "extraNotes", label: t("descriptions"), numeric: false },
    {
      id: "price",
      label: `${t("price")}${currency ? ` (${String(currency).toUpperCase()})` : ""} *`,
      numeric: false,
    },
    { id: "haveScrapItem", label: t("haveScrapItem"), numeric: false },
    {
      id: "minPrice",
      label: `${t("minPrice")}${currency ? ` (${String(currency).toUpperCase()})` : ""}`,
      numeric: false,
    },
    { id: "minQuantity", label: t("minQuantity"), numeric: false },
    { id: "maxQuantity", label: t("maxQuantity"), numeric: false },
    {
      id: "warranty",
      label: t("warrantyMileage") || t("warranty"),
      numeric: false,
    },
    {
      id: "itemWarrantyServiceName",
      label: t("partWarrantyService"),
      numeric: false,
    },
    { id: "shippingMethod", label: t("shippingMethod"), numeric: false },
    { id: "sellBelowCost", label: t("sellBelowCost"), numeric: false },
    { id: "itemEveryMile", label: t("Mileage (km)"), numeric: false },
    {
      id: "itemEveryPeriodType",
      label: t("Number of Days"),
      numeric: false,
    },
  ];

  const previewStats = useMemo(() => {
    const total = imports.length;
    const failed = imports.filter(rowHasErrors).length;
    return { total, failed, valid: total - failed };
  }, [imports]);

  const closeModal = () => {
    setModal(false);
    setFileName("");
    setShowPreview(false);
    setImports([]);
    setSummary(null);
  };

  const downloadSelectedForUpdate = (useAllOnPage = false) => {
    const sourceRows = useAllOnPage ? pageRows : selectedRows;
    if (!sourceRows.length) {
      enqueueSnackbar(<AlertMessages text={t("selectAtLeastOnePart")} />, {
        variant: "warning",
      });
      return;
    }
    setDownloading(true);
    const ids = sourceRows.map((row) => row.id).filter(Boolean);
    ItemsProfileService.exportItemsBySelectedIds(ids)
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `PartsForUpdate.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, {
          variant: "error",
        });
      })
      .finally(() => setDownloading(false));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileName(file ? file.name : "");
  };

  const submitUpdate = () => {
    setLoading(true);
    const total = imports.length;
    ItemsProfileService.updateItemsFromExcel(imports)
      .then((response: ItemExcelUpdateResponse) => {
        if (response.success) {
          const updated =
            response.totalUpdated ??
            response.totalAdded ??
            (typeof response.data === "object" &&
            response.data &&
            !Array.isArray(response.data)
              ? response.data.updated
              : undefined) ??
            previewStats.valid;
          const failed =
            response.totalFailed ??
            (typeof response.data === "object" &&
            response.data &&
            !Array.isArray(response.data)
              ? response.data.failedCount
              : undefined) ??
            previewStats.failed;
          const updatedCount = Number(updated) || 0;
          const failedCount = Number(failed) || 0;
          setSummary({
            total,
            updated: updatedCount,
            failed: failedCount,
          });
          const failedRows = Array.isArray(response.data)
            ? response.data
            : response.data &&
                typeof response.data === "object" &&
                Array.isArray(response.data.failed)
              ? response.data.failed
              : imports.filter(rowHasErrors);
          if (failedRows.length) setImports(failedRows);
          refetch?.();
          enqueueSnackbar(
            <AlertMessages
              text={`${t("total")}: ${total}, ${t("updated")}: ${updatedCount}, ${t("failed")}: ${failedCount}`}
            />,
            { variant: failedCount ? "warning" : "success" },
          );
          if (!failedCount) closeModal();
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={response?.message || t("Something went wrong")}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box>
      <Button
        id="bulk-update-button"
        variant="contained"
        disableElevation
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAnchorEl(event.currentTarget)
        }
        endIcon={<KeyboardArrowDownIcon />}
        className={`bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]`}
        sx={{
          outline: "2px solid",
          color: themeColors?.secondary,
          borderColor: themeColors?.secondary,
          height: 34,
          minHeight: 34,
        }}
      >
        {downloading ? (
          <CircularProgress sx={{ color: themeColors?.secondary }} size={24} />
        ) : (
          t("bulkUpdate")
        )}
      </Button>
      <DropdownMenu
        anchorEl={anchorEl}
        menuItems={[
          {
            label: t("downloadForUpdate"),
            onClick: () => downloadSelectedForUpdate(false),
          },
          {
            label: t("downloadAllOnPageForUpdate"),
            onClick: () => downloadSelectedForUpdate(true),
          },
          {
            label: t("uploadForUpdate"),
            onClick: () => setModal(true),
          },
        ]}
        onClose={() => setAnchorEl(null)}
        hoverColor={themeColors?.secondary}
      />
      {modal && (
        <FormDialog
          open={modal}
          onClose={(event: any, reason: any) => {
            if (event && reason !== "backdropClick" && !loading) closeModal();
          }}
          title={
            (showPreview ? t("updatePreview") : t("uploadForUpdate")) +
            " " +
            t("Items")
          }
          createPopup
          hideActions
          fullWidth={showPreview}
          maxWidth={showPreview ? "xl" : "sm"}
          content={
            <Grid
              container
              className="items-center"
              component="form"
              noValidate={false}
              spacing={1}
              onSubmit={(e) => {
                e.preventDefault();
                setLoading(true);
                const data = new FormData(e.target as HTMLFormElement);
                const file = data.get("file") as File;
                ItemsProfileService.readItemsForUpdate(file)
                  .then((response: ItemExcelUpdatePreviewResponse) => {
                    if (response.success) {
                      const rows = normalizePreviewRows(response.data);
                      setImports(rows);
                      setShowPreview(true);
                      setSummary(null);
                    } else {
                      enqueueSnackbar(
                        <AlertMessages
                          text={response.message || t("Something went wrong")}
                        />,
                        { variant: "error" },
                      );
                    }
                  })
                  .catch((err: any) => {
                    console.error("===", err);
                    enqueueSnackbar(
                      <AlertMessages
                        text={
                          err?.message ||
                          err?.data?.message ||
                          "Something went wrong"
                        }
                      />,
                      { variant: "error" },
                    );
                  })
                  .finally(() => setLoading(false));
              }}
            >
              {!showPreview ? (
                <>
                  <Grid item xs={12} md={8} id="importfile">
                    <input
                      type="file"
                      name="file"
                      required
                      accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                      style={{ color: fileName ? "inherit" : "#fff" }}
                      onChange={handleFileChange}
                      title={fileName}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      type="submit"
                      disabled={loading}
                      margin="dense"
                      value={t("Upload")}
                      InputProps={{
                        startAdornment: loading ? (
                          <CircularProgress
                            size={20}
                            sx={{
                              color: themeColors?.primary,
                              position: "absolute",
                              left: "10px",
                            }}
                          />
                        ) : null,
                      }}
                      sx={{
                        "& div": {
                          borderRadius: "8px",
                          border: "0 !important",
                        },
                        ["& input"]: {
                          fontFamily: "'Saira', sans-serif",
                          fontSize: "14px",
                          lineHeight: "28px",
                          fontWeight: 600,
                          color: "#fff",
                          padding: "0.52rem 2.22rem 0.6rem 2.22rem",
                          boxShadow:
                            "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
                          backgroundImage: `linear-gradient(135deg, ${themeColors?.secondary}, ${themeColors?.secondary + "B3"})`,
                          border: "0 !important",
                          borderRadius: "8px",
                          cursor: "pointer",
                          textTransform: "uppercase",
                          height: "25px",
                        },
                        "& .MuiInputBase-root": { padding: 0 },
                      }}
                    />
                  </Grid>
                </>
              ) : null}
              {showPreview ? (
                <>
                  <Grid item xs={12}>
                    <UploadPreviewTable rows={imports} columns={columns} />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    id="form"
                    className="flex justify-end gap-2"
                  >
                    <Button
                      className="secondary"
                      style={{ color: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={() => closeModal()}
                    >
                      {t("cancel")}
                    </Button>
                    {!summary ? (
                      <Button
                        className="primary"
                        style={{ background: themeColors?.primary }}
                        variant="contained"
                        disableElevation
                        onClick={() => submitUpdate()}
                      >
                        {loading && (
                          <CircularProgress
                            size={15}
                            sx={{
                              color: "#fff",
                              position: "absolute",
                              left: "5px",
                            }}
                          />
                        )}
                        {t("submit")}
                      </Button>
                    ) : null}
                  </Grid>
                </>
              ) : null}
            </Grid>
          }
        />
      )}
    </Box>
  );
};

export default ItemBulkUpdateMenu;
